import { useEffect, useMemo, useRef } from "react";
import { clsx } from "clsx";

import { useRect } from "~/hooks/useRect";
import { fromPx } from "~/utils/components";

import "./Media.css";

export interface MediaProgressValueProps {
  active?: boolean;
  primary?: boolean;
  value: number;
}

const DEFAULT_OFFSET = 4;

export function MediaProgressValue({
  active,
  primary,
  value,
}: MediaProgressValueProps) {
  const offset = useRef<number>(DEFAULT_OFFSET);
  const [ref, { width }] = useRect<HTMLDivElement>();

  const center = useMemo(() => {
    // Round up to prevent subpixel gaps
    return Math.ceil(width * value - offset.current * 2);
  }, [width, value]);

  const end = useMemo(() => {
    // Round up to prevent subpixel gaps
    return Math.ceil(center + offset.current);
  }, [center]);

  const children = useMemo(() => {
    if (!value) return null;

    // https://pqina.nl/blog/animating-width-and-height-without-the-squish-effect/
    return (
      <>
        <div />
        <div style={{ transform: `scaleX(${center})` }} />
        <div style={{ transform: `translateX(${end}px)` }} />
      </>
    );
  }, [center, end, value]);

  // Read the _actual_ offset value from css
  useEffect(() => {
    if (ref.current) {
      offset.current = fromPx(
        getComputedStyle(ref.current).getPropertyValue("--progress-radius"),
      );
    }
  }, [ref]);

  return (
    <div
      ref={ref}
      className={clsx("MediaProgressValue", {
        "MediaProgressValue--active": active,
        "MediaProgressValue--primary": primary,
      })}
    >
      {children}
    </div>
  );
}
